import { 
  Home, 
  User, 
  Timer, 
  TrendingUp,
  Compass,
  Trophy,
  MessageCircle,
  Users,
  Settings,
  Wrench,
  ShieldAlert,
  Book,
  FileText,
  Shield,
  Target,
  LogOut,
  Award,
  Tag,
  UserPlus,
  Upload,
  BookMarked,
  BookOpen,
  Search
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { supabase } from '@/integrations/supabase/client';
import * as React from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  SidebarFooter,
  SidebarRail,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useChats } from '@/hooks/useChats';
import { useNotifications } from '@/hooks/useNotifications';
import { useProfilePrefetch } from '@/hooks/useProfilePrefetch';

const libraryItems = [
  { title: 'Home', url: '/', icon: Home },
  { title: 'Search', url: '/search', icon: Search },
  { title: 'My Profile', url: '/profile', icon: User },
];

const toolsItems = [
  { title: 'Focus Timer', url: '/focus', icon: Timer },
  { title: 'Series Tracker', url: '/series-tracker', icon: BookMarked },
  { title: 'Wrapped', url: '/wrapped', icon: TrendingUp },
  { title: 'Achievements', url: '/achievements', icon: Target },
];

const discoverItems = [
  { title: 'Discover', url: '/discover', icon: Compass },
  { title: 'Browse Tags', url: '/tags', icon: Tag },
  { title: 'Awards', url: '/awards', icon: Award },
];

const socialItems = [
  { title: 'Messages', url: '/inbox', icon: MessageCircle },
  { title: 'Dino Duos', url: '/dino-duos', icon: Users },
  { title: 'Leaderboards', url: '/leaderboards', icon: Trophy },
];

const settingsItems = [
  { title: 'Settings', url: '/settings', icon: Settings },
  { title: 'Preferences', url: '/settings/preferences', icon: Wrench },
];

const legalItems = [
  { title: 'Community Guidelines', url: '/community-guidelines', icon: Book },
  { title: 'Terms of Service', url: '/terms-of-service', icon: FileText },
  { title: 'Privacy Policy', url: '/privacy-policy', icon: Shield },
];

const adminItems = [
  { title: 'Enrichment', url: '/admin/enrichment', icon: ShieldAlert },
  { title: 'Moderation', url: '/admin/moderation', icon: ShieldAlert },
  { title: 'Awards', url: '/admin/awards', icon: Trophy },
  { title: 'Book Import', url: '/admin/book-import', icon: Upload },
  { title: 'Series Management', url: '/admin/series-management', icon: BookOpen },
  { title: 'Author Seeds', url: '/admin/author-seeds', icon: UserPlus },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isAdmin } = useIsAdmin();
  const { getTotalUnreadCount } = useChats();
  const { unreadCount } = useNotifications();
  const { prefetchProfile } = useProfilePrefetch();
  const [username, setUsername] = React.useState<string>('');
  const [pendingInvites, setPendingInvites] = React.useState(0);
  const currentPath = location.pathname;
  const collapsed = state === 'collapsed';

  // Prefetch profile on hover
  const handleProfilePrefetch = React.useCallback(() => {
    if (username) {
      prefetchProfile(username);
    }
  }, [username, prefetchProfile]);

  // Fetch username and pending invites
  React.useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      
      // Fetch username
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .maybeSingle();
      
      if (profileData?.username) {
        setUsername(profileData.username);
      }

      // Fetch pending buddy read invitations
      const { data: inviteData } = await supabase
        .from('buddy_read_members')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .eq('status', 'invited');
      
      setPendingInvites(inviteData?.length || 0);
    };
    
    fetchUserData();

    // Subscribe to buddy read invitation changes
    const channel = supabase
      .channel('sidebar-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'buddy_read_members',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          fetchUserData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const isActive = (path: string) => {
    if (path === '/') return currentPath === '/';
    return currentPath.startsWith(path);
  };

  // Update library items with dynamic profile URL
  const profileUrl = username ? `/u/${username}` : '/onboarding';
  const updatedLibraryItems = libraryItems.map(item => 
    item.url === '/profile' ? { ...item, url: profileUrl } : item
  );

  const renderMenuItem = (item: typeof libraryItems[0], badgeCount?: number) => {
    const Icon = item.icon;
    const active = isActive(item.url);
    const isProfileLink = item.title === 'My Profile';

    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton asChild>
          <NavLink 
            to={item.url} 
            end={item.url === '/'} 
            className="hover:bg-secondary-light/30 dark:hover:bg-muted text-secondary-foreground dark:text-card-foreground transition-all duration-200 ease-in-out group relative"
            activeClassName="bg-primary text-primary-foreground font-medium"
            onMouseEnter={isProfileLink ? handleProfilePrefetch : undefined}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center relative">
                <Icon className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                {/* Badge for collapsed mode - show as dot on icon */}
                {collapsed && badgeCount !== undefined && badgeCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 bg-[hsl(var(--burnt-orange))] rounded-full animate-scale-in" />
                )}
                {!collapsed && (
                  <span className="animate-fade-in transition-opacity duration-200">
                    {item.title}
                  </span>
                )}
              </div>
              {/* Badge for expanded mode - show count */}
              {!collapsed && badgeCount !== undefined && badgeCount > 0 && (
                <Badge 
                  className="ml-auto h-5 min-w-5 text-xs px-1.5 flex items-center justify-center animate-scale-in bg-[hsl(var(--burnt-orange))] text-white hover:bg-[hsl(var(--burnt-orange-light))]"
                >
                  {badgeCount >= 99 ? '99+' : badgeCount}
                </Badge>
              )}
            </div>
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  if (!user) return null;

  return (
    <Sidebar
      className="transition-all duration-300 ease-in-out bg-secondary dark:bg-card text-secondary-foreground dark:text-card-foreground border-0 border-transparent pt-0"
      collapsible="icon"
    >
      <SidebarContent className="transition-opacity duration-200 bg-transparent gap-0 pt-0">
        {/* My Library */}
        <SidebarGroup className="px-2 pb-2 pt-6">
          <SidebarGroupContent>
            <SidebarMenu aria-label="My Library">
              {updatedLibraryItems.map((item) => renderMenuItem(item))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Discover */}
        <SidebarGroup className="py-1.5 px-2">
          <SidebarGroupLabel className={`${collapsed ? 'sr-only' : 'transition-opacity duration-200'} text-secondary-foreground/70 dark:text-card-foreground/70 font-semibold`}>
            Discover
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {discoverItems.map((item) => renderMenuItem(item))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Social */}
        <SidebarGroup className="py-1.5 px-2">
          <SidebarGroupLabel className={`${collapsed ? 'sr-only' : 'transition-opacity duration-200'} text-secondary-foreground/70 dark:text-card-foreground/70 font-semibold`}>
            Social
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {socialItems.map((item) => {
                let badgeCount: number | undefined;
                if (item.title === 'Messages') {
                  badgeCount = getTotalUnreadCount();
                } else if (item.title === 'Dino Duos') {
                  badgeCount = pendingInvites;
                }
                return renderMenuItem(item, badgeCount);
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Tools */}
        <SidebarGroup className="py-1.5 px-2">
          <SidebarGroupLabel className={`${collapsed ? 'sr-only' : 'transition-opacity duration-200'} text-secondary-foreground/70 dark:text-card-foreground/70 font-semibold`}>
            Tools
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {toolsItems.map((item) => renderMenuItem(item))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Settings - Collapsible */}
        <Collapsible defaultOpen={false} className="group/collapsible">
          <SidebarGroup className="py-1.5 px-2">
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="flex w-full items-center justify-between hover:bg-secondary-light/30 dark:hover:bg-muted rounded-md px-2 py-1.5 transition-all duration-200 ease-in-out text-secondary-foreground dark:text-card-foreground">
                {!collapsed && (
                  <>
                    <span className="transition-opacity duration-200">Settings</span>
                    <ChevronDown className="ml-auto h-4 w-4 transition-transform duration-300 ease-in-out group-data-[state=open]/collapsible:rotate-180" />
                  </>
                )}
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent className="transition-all duration-300 ease-in-out data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <SidebarGroupContent>
                <SidebarMenu>
                  {settingsItems.map((item) => renderMenuItem(item))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        {/* Legal - Collapsible */}
        <Collapsible defaultOpen={false} className="group/collapsible">
          <SidebarGroup className="py-1.5 px-2">
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="flex w-full items-center justify-between hover:bg-secondary-light/30 dark:hover:bg-muted rounded-md px-2 py-1.5 transition-all duration-200 ease-in-out text-secondary-foreground dark:text-card-foreground">
                {!collapsed && (
                  <>
                    <span className="transition-opacity duration-200">Legal</span>
                    <ChevronDown className="ml-auto h-4 w-4 transition-transform duration-300 ease-in-out group-data-[state=open]/collapsible:rotate-180" />
                  </>
                )}
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent className="transition-all duration-300 ease-in-out data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <SidebarGroupContent>
                <SidebarMenu>
                  {legalItems.map((item) => renderMenuItem(item))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        {/* Admin (only for admins) - Collapsible */}
        {isAdmin && (
          <Collapsible defaultOpen={false} className="group/collapsible">
            <SidebarGroup className="py-1.5 px-2">
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger className="flex w-full items-center justify-between hover:bg-secondary-light/30 dark:hover:bg-muted rounded-md px-2 py-1.5 transition-all duration-200 ease-in-out text-secondary-foreground dark:text-card-foreground">
                  {!collapsed && (
                    <>
                      <span className="transition-opacity duration-200">Admin</span>
                      <ChevronDown className="ml-auto h-4 w-4 transition-transform duration-300 ease-in-out group-data-[state=open]/collapsible:rotate-180" />
                    </>
                  )}
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent className="transition-all duration-300 ease-in-out data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <SidebarGroupContent>
                  <SidebarMenu>
                    {adminItems.map((item) => renderMenuItem(item))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}
      </SidebarContent>

      {/* Footer with Sign Out */}
      <SidebarFooter className="border-t border-secondary-light/30 dark:border-border transition-all duration-200 bg-transparent">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={async () => {
                await signOut();
                navigate('/');
              }}
              className="hover:bg-secondary-light/30 dark:hover:bg-muted transition-all duration-200 ease-in-out text-primary hover:text-primary-light dark:text-card-foreground group"
            >
              <LogOut className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
              {!collapsed && (
                <span className="animate-fade-in transition-opacity duration-200">
                  Sign Out
                </span>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
